import "./closePeriod.scss";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { Checkbox, Collapse, FormControlLabel, Radio, RadioGroup } from "@mui/material";
import ArrowRight from "assets/img/svg/arrowRight";
import ModalBox from "components/Modal";
import DocumentationLink from "components/shared/DocumentationLink";
import MessageBlock from "components/shared/MessageBlock";
import ScrollShadowWrapper from "components/shared/ScrollShadowWrapper";
import StyledButton from "components/shared/StyledButton";
import { Body, BodyHighlight, H3, SmallBodyHighlight } from "helpers/TypographyComponents";
import { formatNumber } from "helpers/utils";
import { useCompanySettings } from "hooks/useCompanySettings";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { fetchUnpaidBeneficiariesInformation, updatePaymentStatusBulk } from "store/managementStore";
import type { IPaymentResource } from "store/paymentSlice";
import { addToPayment, EPaymentResourceType, EPaymentType } from "store/paymentSlice";
import {
    fetchClosePeriod,
    fetchDeferredPaymentAccounting,
    fetchPeriods,
    putMinimum,
    putPeriodClosedWithCarryForward,
    setActivePeriod,
    setClosePeriodIdleStatus,
} from "store/periodStore";
import type { AppDispatch } from "store/store";
import { useAppSelector } from "store/store";

import { Pending } from "..";
import { ClosePeriodActions } from "../ClosePeriodActions";

type TProps = {
    close: () => void;
    onBeneficiaryTab: () => void;
};

export enum EClosePeriodAction {
    CARRY_FORWARD = 0,
    MARK_AS_PAID = 1,
    DO_NOTHING = 2,
}

const ClosePeriod = ({ close, onBeneficiaryTab }: TProps) => {
    const [t] = useTranslation();
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const { activePeriod, closePeriodStatus } = useAppSelector((state) => state.period);
    const { unpaidBeneficiaries, fullyUnpaidBeneficiaries, partiallyPaidBeneficiaries } = useAppSelector(
        (state) => state.management,
    );
    const [page, setPage] = useState(0);
    const [isLoadingRightButton, setIsLoadingRightButton] = useState(true);

    const { getCompanySettings } = useCompanySettings();
    const usesStock = getCompanySettings("uses_stock");

    const unpaidBeneficiariesCount = fullyUnpaidBeneficiaries.length;

    const checkboxesData = [
        {
            label: t("Management.ClosePeriodModal.ClosePeriodCheckbox2"),
            checked: false,
            show: usesStock,
        },
        {
            label: t("Management.ClosePeriodModal.ClosePeriodCheckbox1"),
            checked: false,
            show: true,
        },
        {
            label: t("Management.ClosePeriodModal.ClosePeriodCheckbox4"),
            checked: false,
            show: true,
        },
    ];

    const [checkboxes, setCheckboxes] = useState(checkboxesData);
    const [closePeriodAction, setClosePeriodAction] = useState<EClosePeriodAction | undefined>(undefined);

    const handleFetchUnpaidBeneficiaries = async () => {
        if (!activePeriod) return;

        const response = await dispatch(fetchUnpaidBeneficiariesInformation(activePeriod));
        if (response.meta.requestStatus === "fulfilled") {
            setIsLoadingRightButton(false);
        }
    };

    useEffect(() => {
        handleFetchUnpaidBeneficiaries();
    }, []);

    const handleClosePeriod = async () => {
        if (!activePeriod) return;
        if (closePeriodAction == EClosePeriodAction.CARRY_FORWARD) {
            await dispatch(putPeriodClosedWithCarryForward({ periodId: activePeriod, isClosedWithCarryForward: true }));
        } else if (closePeriodAction == EClosePeriodAction.MARK_AS_PAID) {
            const resources: IPaymentResource[] = unpaidBeneficiaries.map((item) => {
                const leftToPay = item.netToPay - (item.stageAndCommitted ?? 0);
                return {
                    type:
                        item.netToPay - leftToPay > 0
                            ? EPaymentResourceType.PARTIAL_PAYMENT
                            : EPaymentResourceType.BENEFICIARY,
                    id: item.id,
                    amount: +leftToPay,
                    periodId: activePeriod,
                    underTheMinimum: 0, // We skip the check on under the minimum as we want to force mark as paid (close period)
                };
            });

            const data = {
                method: EPaymentType.MANUAL,
                paymentDate: new Date(),
                resources,
            };

            await dispatch(addToPayment({ data }));
        }

        const response = await dispatch(fetchClosePeriod(activePeriod));
        if (response.meta.requestStatus === "fulfilled") {
            setActivePeriod(response.payload.newPeriodId);
        }
    };

    const handleChangePage = (page: number) => {
        setPage(page);
    };

    const handleCheck = (checked: boolean, index: number) => {
        const newCheckboxes = [...checkboxes.filter((item) => item.show)];
        newCheckboxes[index].checked = checked;
        setCheckboxes(newCheckboxes);
    };

    const handleClosePeriodAction = (value: string) => {
        setClosePeriodAction(+value);
    };

    const showDownloadDeferredAmount = useMemo(() => {
        if (typeof closePeriodAction !== "undefined" && closePeriodAction !== EClosePeriodAction.MARK_AS_PAID)
            return true;
        else return false;
    }, [closePeriodAction]);

    const handleClickAllBeneficiaries = () => {
        onBeneficiaryTab();
        close();
    };

    useEffect(() => {
        if (closePeriodStatus === "failed") {
            dispatch(setClosePeriodIdleStatus());
            close();
        }
        if (closePeriodStatus === "succeeded") {
            dispatch(setClosePeriodIdleStatus());
            dispatch(fetchPeriods()).then(() => close());
        }
    }, [closePeriodStatus]);

    const onExportDeferredPayment = () => {
        dispatch(fetchDeferredPaymentAccounting(activePeriod as number));
    };

    return (
        <>
            <div className="close-period-modal-content">
                {page == 0 && (
                    <div className="close-period-modal-content-checkboxes">
                        <H3>{t("Management.ClosePeriodModal.ClosePeriodConfirmText")}</H3>
                        {checkboxes
                            .filter((item) => item.show)
                            .map((checkbox, index) => (
                                <div className="close-period-modal-content-checkbox" key={index}>
                                    <Checkbox
                                        id={`checkbox-${index}`}
                                        checked={checkbox.checked}
                                        onChange={(_, checked) => handleCheck(checked, index)}
                                    />
                                    <Body tag="label" htmlFor={`checkbox-${index}`}>
                                        {checkbox.label}
                                    </Body>
                                </div>
                            ))}
                    </div>
                )}
                {unpaidBeneficiariesCount > 0 && page == 1 && (
                    <div className="close-period-modal-content-radios">
                        <H3>
                            {unpaidBeneficiariesCount}{" "}
                            {t("Management.ClosePeriodModal.ClosePeriodNotNullBalance", {
                                count: unpaidBeneficiariesCount,
                            })}
                        </H3>
                        <Collapse in={fullyUnpaidBeneficiaries.length > 0}>
                            <ScrollShadowWrapper className="close-period-modal-content-authors">
                                {fullyUnpaidBeneficiaries.map((beneficiary, index) => (
                                    <div className="close-period-modal-content-author" key={index}>
                                        <BodyHighlight
                                            color="black"
                                            style={{ textDecoration: "underline" }}
                                            onClick={() => navigate(`/beneficiars/${beneficiary.id}`)}
                                        >
                                            {beneficiary.surname.toLocaleUpperCase()} {beneficiary.name ?? ""}
                                        </BodyHighlight>
                                        <Body color="grey">
                                            {/* {t("Management.GrossDuty")}: */}
                                            <Body tag="span">
                                                {" "}
                                                {formatNumber(beneficiary.unpaidAmount / 100, {
                                                    isCurrency: true,
                                                    always2Digit: true,
                                                })}
                                            </Body>
                                        </Body>
                                    </div>
                                ))}
                            </ScrollShadowWrapper>
                        </Collapse>
                        <SmallBodyHighlight
                            color="info"
                            className="close-period-modal-content-action-right"
                            onClick={handleClickAllBeneficiaries}
                        >
                            {t("Management.ClosePeriodModal.SeeAuthors", {
                                count: unpaidBeneficiariesCount,
                            })}
                            <ArrowRight color="currentColor" />
                        </SmallBodyHighlight>
                        <H3 style={{ marginTop: "16px" }}>
                            {t("Management.ClosePeriodModal.ClosePeriodNotNullBalanceAction")}
                        </H3>
                        <RadioGroup onChange={(_, value) => handleClosePeriodAction(value)}>
                            <FormControlLabel
                                value={EClosePeriodAction.CARRY_FORWARD}
                                control={<Radio checkedIcon={<CheckCircleIcon style={{ width: "15px" }} />} />}
                                label={<Body>{t("Management.ClosePeriodModal.Report")}</Body>}
                            />
                            <FormControlLabel
                                value={EClosePeriodAction.MARK_AS_PAID}
                                control={<Radio checkedIcon={<CheckCircleIcon style={{ width: "15px" }} />} />}
                                label={<Body>{t("Management.ClosePeriodModal.MarkAsPay")}</Body>}
                            />
                            <FormControlLabel
                                value={EClosePeriodAction.DO_NOTHING}
                                control={<Radio checkedIcon={<CheckCircleIcon style={{ width: "15px" }} />} />}
                                label={<Body>{t("Management.ClosePeriodModal.DoNothing")}</Body>}
                            />
                        </RadioGroup>
                        <Collapse in={showDownloadDeferredAmount} timeout={"auto"}>
                            <div style={{ display: "flex", justifyContent: "flex-end" }}>
                                <div className="download-deferred-amount" onClick={onExportDeferredPayment}>
                                    <FileDownloadOutlinedIcon sx={{ width: "18px", height: "18px" }} />
                                    <span>{t("Management.ExportDeferredAmount")}</span>
                                </div>
                            </div>
                        </Collapse>
                        {partiallyPaidBeneficiaries.length > 0 &&
                            closePeriodAction == EClosePeriodAction.CARRY_FORWARD && (
                                <MessageBlock
                                    type="warning"
                                    style={{ marginTop: "0.5rem" }}
                                    text={t("Management.ClosePeriodModal.WarningPartiallyPaid")}
                                />
                            )}
                    </div>
                )}
                <ClosePeriodActions
                    handleChangePage={handleChangePage}
                    page={page}
                    handleClosePeriod={handleClosePeriod}
                    closePeriodAction={closePeriodAction}
                    unpaidBeneficiariesCount={unpaidBeneficiariesCount}
                    checkboxes={checkboxes}
                    isLoadingRightButton={isLoadingRightButton}
                    close={close}
                />
            </div>
            {closePeriodStatus === "pending" && (
                <ModalBox open={closePeriodStatus === "pending"} onClose={() => {}} showCloseButton={false}>
                    <Pending phrase={t("Management.ClosingPeriod")} />
                </ModalBox>
            )}
        </>
    );
};

export default ClosePeriod;
